<script setup lang='ts'>
import { useQuery } from 'vue-query'
import { useAnnouncements } from '~~/composables/api/useAnnouncements'
import { useAlertStore } from '~/stores/alert'
import { useAuthStore } from '~/stores/auth'
import { useSettingsStore } from '~/stores/settings'
import { useAdminSettings } from '~/composables/api/useAdminSettings'
import { useEventFeatures } from '~/composables/useEventFeatures'
import { NDropdown, NIcon } from 'naive-ui'

const props = defineProps<{
  opened: boolean
}>()

defineEmits<{
  (event: 'close'): void
}>()

const route = useRoute()
const { getAll } = useAnnouncements()
const alertStore = useAlertStore()
const authStore = useAuthStore()
const { captureException } = useSentry()
const { getAll: getAllSettings } = useAdminSettings()
const settingsStore = useSettingsStore()

const user = computed(() => authStore.user)
const eventFeatures = useEventFeatures()
const navigationSpace = computed(() => {
  if (!route.meta.auctionNavigation)
    return route.meta.spaceName

  if (authStore.isAdmin)
    return 'admin'
  if (authStore.isJudge)
    return 'judge'
  if (authStore.isViewer)
    return 'system'
  return 'dashboard'
})

const { data: settingsArray, isFetched: settingsLoaded, refetch: fetchSettings } = useQuery({
  queryKey: ['adminSettings'],
  queryFn: () => {
    try {
      return getAllSettings()
    }
    catch (e) {
      console.error(e)
      captureException(e)
      return []
    }
  }, // getAllSettings().then(a => settingsArrayToRecord(a)),
  initialData: [],
})

if (!settingsLoaded.value)
  fetchSettings.value()

watch(settingsArray, (a) => {
  settingsStore.setSettings(a)
})

const { data: announcements, refetch: refetchAnnouncements } = useQuery({
  queryKey: ['announcements'],
  queryFn: getAll,
  throwOnError: (e: any) => {
    console.error(e)
    captureException(e)
    return false
  },
  initialData: [],
})

watch(() => user.value?.id, (newId, oldId) => {
  if (newId && newId !== oldId && refetchAnnouncements.value) {
    console.log('Refetching announcements after auth state change...')
    refetchAnnouncements.value()
  }
})

const links = computed(() => {
  switch (navigationSpace.value) {
    case 'dashboard': {
      return [
        {
          title: 'My Team',
          url: '/my-team',
        },
        {
          title: 'Rules',
          url: '/rules',
        },
        {
          title: 'Schedule',
          url: '/schedule',
        },
        {
          title: 'Auction',
          url: eventFeatures.auction.value ? '/auction' : undefined,
        },
        {
          title: 'Leaderboard',
          url: eventFeatures.scoreboard.value ? '/leaderboard' : undefined,
        },
        {
          title: 'Raffle',
          url: eventFeatures.raffle.value ? '/raffles' : undefined,
        },
        {
          title: 'Announcements',
          url: '/announcements',
          count: announcements.value?.filter(item => item.published)?.length ?? 0,
        },
      ]
    }

    case 'admin': {
      return [
        {
          title: 'Dashboard',
          url: '/admin/dashboard',
        },
        {
          title: 'Registration',
          url: '/admin/registration/overview',
          matchPrefix: '/admin/registration',
        },
        {
          title: 'Pending',
          url: '/admin/pending',
        },
        {
          title: 'Teams',
          url: '/admin/teams',
        },
        {
          title: 'Anglers',
          url: '/admin/anglers',
        },
        // {
        //   title: 'Participants',
        //   url: '/admin/participants',
        // },
        {
          title: 'Announcements',
          url: '/admin/announcements',
        },
        {
          title: 'Contests',
          children: [
            { title: 'Auction', key: 'auction', url: '/admin/auction' },
            { title: 'Raffles', key: 'raffles', url: '/admin/raffles' },
            { title: 'Raffle buyers', key: 'raffle-buyers', url: '/admin/raffle-buyers' },
            { title: 'Winners', key: 'winners', url: '/admin/winners' },
            { title: 'Random Winners', key: 'random-winners', url: '/admin/random-winners' },
            { title: 'Reel of Names', key: 'reel-of-names', url: '/admin/reel-of-names' },
          ],
        },
        {
          title: 'Judges',
          url: '/admin/judges',
        },
        {
          title: 'Scores',
          url: '/admin/scores',
        },
        {
          title: 'Leaderboard',
          url: '/public/leaderboard',
          live: true,
        },
        // {
        //   title: 'Photos',
        //   url: '/admin/photos',
        // },
        {
          title: 'Guests',
          url: '/admin/guests',
        },
        {
          title: 'Notes',
          url: '/admin/notes',
        },
      ]
    }

    case 'system': {
      return [
        ...(authStore.isAdmin
          ? [{
              title: 'Events',
              url: '/system/events',
            }]
          : []),
        {
          title: 'Users',
          url: '/system/users',
        },
      ]
    }

    case 'judge': {
      return [
        {
          title: 'Anglers',
          url: '/judges/anglers',
        },
        {
          title: 'Scoring',
          url: '/judges/scoring',
        },
        {
          title: 'Raffles',
          url: '/judges/raffles',
        },
        {
          title: 'Guests',
          url: '/judges/guests',
        },
      ]
    }

    default: {
      return []
    }
  }
})

// For desktop, use links as-is (with children for dropdowns)
const desktopLinks = computed(() => links.value)

// For mobile, flatten dropdown items into individual links
const mobileLinks = computed(() => {
  const result = []
  for (const link of links.value) {
    if (link.children) {
      // On mobile, expand dropdown items as individual links
      for (const child of link.children) {
        result.push({
          title: child.title,
          url: child.url,
        })
      }
    } else {
      result.push(link)
    }
  }
  return result
})

// Handle dropdown item selection
const handleDropdownSelect = (key: string, option: any) => {
  if (option.url) {
    navigateTo(option.url)
    // Emit close to close mobile menu if open
    const emit = getCurrentInstance()?.emit
    emit?.('close')
  }
}

// Custom function to check if a link should be active
function isLinkActive(link: any) {
  if (link.matchPrefix) {
    return route.path.startsWith(link.matchPrefix)
  }
  return route.path === link.url
}

// Check if any child of dropdown is active
function isDropdownActive(link: any) {
  if (!link.children) return false
  return link.children.some((child: any) =>
      child.url && (route.path === child.url || route.path.startsWith(child.url + '/'))
  )
}

const style = computed(() => ({
  wrapper: [
    'lg-max:theme-neutral-light dark:lg-max:theme-green-light lg-max:bb lg-max:rounded-2xl lg-max:shadow-lg lg-max:overflow-y-auto lg-max:max-h-[24rem]',
    'nav-wrapper overscroll-none absolute top-full mt-1 bg-t-bg left-5 right-5 justify-center lg:pointer-events-none z-[10]',
    'lg:bottom-0 lg:mt-0 lg:left-[240px] lg:bg-transparent lg:right-[240px] lg:top-0 lg:flex lg:items-center',
    {
      'lg-max:hidden': !props.opened,
    },
  ],
  links: [
    'lg-max:w-full',
    'pointer-events-auto gap-2 items-center grid mx-auto whitespace-nowrap overflow-x-auto p-2 w-full no-scrollbar',
    'lg:flex lg:h-full lg:px-10 lg:w-auto lg:gap-7 lg:py-0 lg:justify-start',
  ],
  link: (hasUrl: boolean) => [
    'lg-max:w-full',
    'group pointer-events-auto p-0.5 bg-transparent rounded-[0.875rem] transition-colors relative text-t-text-light',
    {
      'hover:text-t-text': hasUrl,
    },
  ],
  linkActive: 'is-active theme-coral !bg-t-bg !text-t-text',
  linkInner: 'flex items-center justify-center border border-t-border lg:border-transparent group-[.is-active]:border-t-border lg-max:px-4 lg-max:py-2 lg:group-[.is-active]:px-4 lg:group-[.is-active]:py-2 rounded-xl transition-[padding]',
  title: 'text-xl leading-normal heading',
  soon: 'text-base heading opacity-50 ml-1',
  dropdownIcon: 'ml-1 transition-transform duration-200',
  dropdownTrigger: 'w-full cursor-pointer',
}))
</script>

<template>
  <div v-if="user?.id && desktopLinks && desktopLinks.length" :class="style.wrapper">
    <div :class="style.links">
      <!-- Desktop Navigation -->
      <template v-for="link in desktopLinks" :key="link.title" class="hidden lg:contents">
        <!-- Dropdown Menu -->
        <div v-if="link.children" class="hidden lg:block">
        <n-dropdown
            :options="link.children"
            @select="handleDropdownSelect"
            trigger="hover"
            placement="bottom-start"
            :show-arrow="true"
        >
          <div
              :class="[
              style.link(true),
              { [style.linkActive]: isDropdownActive(link) },
              style.dropdownTrigger
            ]"
          >
            <div :class="style.linkInner">
              <CommonIndicator v-if="link.count" :value="link.count" class="theme-coral" border />
              <span :class="style.title">
                {{ link.title }}
              </span>
              <n-icon :class="style.dropdownIcon">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                  <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                </svg>
              </n-icon>
              <span v-if="link.live" :class="style.soon">Live</span>
            </div>
          </div>
        </n-dropdown>
        </div>
        <!-- Regular Desktop Link -->
        <NuxtLink
            v-else
            :disabled="!link.url"
            :class="[
            'hidden lg:block',
            style.link(!!link.url),
            { [style.linkActive]: isLinkActive(link) }
          ]"
            :to="link.url"
            @click="$emit('close')"
        >
          <div :class="style.linkInner">
            <CommonIndicator v-if="link.count" :value="link.count" class="theme-coral" border />
            <span :class="style.title">
              {{ link.title }}
            </span>
            <span v-if="!link.url" :class="style.soon">Soon</span>
            <span v-if="link.live" :class="style.soon">Live</span>
          </div>
        </NuxtLink>
      </template>

      <!-- Mobile Navigation (Flat List) -->
      <template v-for="link in mobileLinks" :key="`mobile-${link.title}`">
        <NuxtLink
            :disabled="!link.url"
            :class="[
            'lg:hidden',
            style.link(!!link.url),
            { [style.linkActive]: isLinkActive(link) }
          ]"
            :to="link.url"
            @click="$emit('close')"
        >
          <div :class="style.linkInner">
            <CommonIndicator v-if="link.count" :value="link.count" class="theme-coral" border />
            <span :class="style.title">
              {{ link.title }}
            </span>
            <span v-if="!link.url" :class="style.soon">Soon</span>
            <span v-if="link.live" :class="style.soon">Live</span>
          </div>
        </NuxtLink>
      </template>

    </div>
  </div>
</template>

<style lang="scss" scoped>
.nav-wrapper {
  -webkit-overflow-scrolling: touch;
}
</style>
